'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Column, Pagination, Section, TransformedNewsItem } from '@/lib/types/pageBuilder';
import { DynamicTabInfo } from './TabBarSection';
import ColumnContentRenderer from './ColumnContentRenderer';
import type { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
import { getStickyStyles } from '@/lib/componentutills';
import PaginationPanel from './PaginationPanel';
import AstroPagination from '@/components/component_astro_crick/pagination';
const colSpanMap: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12'
};
const mdColSpanMap: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12'
};
const lgColSpanMap: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12'
};
interface ColumnRendererProps {
  column: Column;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>; // Map of tabBar ID to dynamic tabs
  isLastColumn?: boolean;
  includeSectionsData?: Map<string, Section>; // Map of section slug to section data
  includeSectionApiData?: IncludeSectionApiData;
  parentColumnData?: TransformedNewsItem[]; // Data inherited from parent column's data_binding
  dataStartIndex?: number; // Start index for sequential data consumption
  /** Mark first component's image as priority for LCP optimization */
  priority?: boolean;
}
export default function ColumnRenderer({
  column,
  collectionsData,
  dynamicTabsMap,
  isLastColumn,
  includeSectionsData,
  includeSectionApiData,
  parentColumnData,
  dataStartIndex = 0,
  priority = false
}: ColumnRendererProps) {
  // Skip hidden columns
  if (column.hidden) {
    return null;
  }

  // hideWhenEmpty: if true, hide the column when its data binding returns no data
  if (column.hideWhenEmpty) {
    const bindingKey = column.data_binding?.api_endpoint || column.data_binding?.collection_id;
    const sliderKey = (column.slider as any)?.data_binding?.api_endpoint || (column.slider as any)?.data_binding?.collection_id;
    const key = bindingKey ? normalizeApiEndpoint(bindingKey) : sliderKey ? normalizeApiEndpoint(sliderKey) : null;
    const data = key ? collectionsData.get(key) : parentColumnData;
    const contentItems = data?.filter(item => !item._isCollectionMeta) || [];
    if (contentItems.length === 0) {
      return null;
    }
  }

  // Check if this column has pagination enabled
  const hasPagination = column.pagination?.enabled && !column.tabBar;
  const pagination = column.pagination as Pagination | undefined ?? undefined;
  const loopCountFromJson = column.components?.find(c => c.data_config?.mode === 'loop' && typeof c.data_config.loop_count === 'number')?.data_config?.loop_count;
  const pageSize = pagination?.pageSize || loopCountFromJson || 10;
  const columnRef = useRef<HTMLDivElement>(null);

  // Read the page query param from window.location instead of useSearchParams().
  // Reading useSearchParams() in a Client Component forces the enclosing Suspense
  // boundary to BAILOUT_TO_CLIENT_SIDE_RENDERING, which kills SSR for every column
  // on the page. Page=1 is the safe SSR default; the pagination effect below
  // reads the URL on hydration and swaps if needed.
  const getInitialPage = useCallback((): number => {
    if (typeof window === 'undefined') return 1;
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed >= 1) return parsed;
    }
    return 1;
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState<TransformedNewsItem[]>([]);
  const [totalItems, setTotalItems] = useState(100);
  const [loading, setLoading] = useState(false);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [initialized, setInitialized] = useState(false);
  const rawDataBindingKey = column.data_binding?.api_endpoint || column.data_binding?.collection_id;
  // Normalize the endpoint to match how data is stored in collectionsData
  const dataBindingKey = rawDataBindingKey ? normalizeApiEndpoint(rawDataBindingKey) : undefined;

  // Get initial items for this column's data binding
  const initialItems = dataBindingKey ? collectionsData.get(dataBindingKey) || [] : [];
  const getApiEndpoint = useCallback(() => {
    if (column.data_binding?.api_endpoint) {
      return column.data_binding.api_endpoint;
    }
    if (column.data_binding?.collection_id) {
      return column.data_binding.collection_id;
    }
    if (column.tabBar?.dynamicConfig?.content_api_endpoint) {
      return column.tabBar.dynamicConfig.content_api_endpoint;
    }
    if (column.tabBar?.dynamicConfig?.api_endpoint) {
      return column.tabBar.dynamicConfig.api_endpoint;
    }
    return null;
  }, [column.data_binding?.api_endpoint, column.data_binding?.collection_id, column.tabBar?.dynamicConfig]);
  const apiEndpoint = getApiEndpoint();
  const fetchPaginatedData = useCallback(async (page: number, size: number, append: boolean = false) => {
    if (!hasPagination || !pagination) {
      return;
    }
    const endpoint = getApiEndpoint();
    if (!endpoint) {
      return;
    }
    setLoading(true);
    try {
      const offset = (page - 1) * size;
      const fetchUrl = `/api/paginated-collection?endpoint=${encodeURIComponent(endpoint)}&limit=${size}&offset=${offset}`;
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Failed to fetch paginated data');
      const data_0 = await response.json();
      if (data_0.items) {
        if (append || pagination.type === 'loadmore' || pagination.type === 'infinite') {
          setItems(prev => [...prev, ...data_0.items]);
        } else {
          setItems(data_0.items);
        }
        if (data_0['total-count']) {
          setTotalItems(data_0['total-count']);
        }
      }
    } catch (error) {
      console.error('Error fetching paginated data:', error);
    } finally {
      setLoading(false);
    }
  }, [getApiEndpoint, hasPagination, pagination]);
  const handlePageChange = useCallback((page_0: number) => {
    if (!hasPagination || !pagination) {
      return;
    }
    if (pagination.type === 'numbered') {
      setCurrentPage(page_0);
      fetchPaginatedData(page_0, currentPageSize, false);

      // Update URL with page parameter
      try {
        const url = new URL(window.location.href);
        if (page_0 === 1) {
          url.searchParams.delete('page');
        } else {
          url.searchParams.set('page', page_0.toString());
        }
        window.history.pushState({}, '', url.toString());
      } catch (e) {
        console.error('[Pagination] Failed to update URL:', e);
      }
      if (columnRef.current) {
        columnRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      setCurrentPage(page_0);
      fetchPaginatedData(page_0, currentPageSize, true);
    }
  }, [currentPageSize, fetchPaginatedData, hasPagination, pagination]);
  const handlePageSizeChange = useCallback((size_0: number) => {
    if (!hasPagination) {
      return;
    }
    setCurrentPageSize(size_0);
    setCurrentPage(1);
    setItems([]);
    fetchPaginatedData(1, size_0, false);
  }, [fetchPaginatedData, hasPagination]);

  // Infinite scroll handler - debounced with RAF to avoid forced reflows
  useEffect(() => {
    if (!hasPagination || !pagination || pagination.type !== 'infinite') return;
    let scrollRafId: number | null = null;
    let debounceTimer: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      // Debounce scroll events
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Use RAF to batch DOM reads
        if (scrollRafId) cancelAnimationFrame(scrollRafId);
        scrollRafId = requestAnimationFrame(() => {
          const scrollHeight = document.documentElement.scrollHeight;
          const scrollTop = window.scrollY;
          const clientHeight = window.innerHeight;
          if (scrollHeight - scrollTop - clientHeight < 200 && !loading) {
            const nextPage = currentPage + 1;
            const maxPages = Math.min(Math.ceil(totalItems / currentPageSize), 500);
            if (nextPage <= maxPages) {
              setCurrentPage(nextPage);
              fetchPaginatedData(nextPage, currentPageSize, true);
            }
          }
        });
      }, 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafId) cancelAnimationFrame(scrollRafId);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [currentPage, currentPageSize, fetchPaginatedData, hasPagination, loading, pagination, totalItems]);
  const prevApiEndpointRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hasPagination) return;
    if (prevApiEndpointRef.current !== null && prevApiEndpointRef.current !== apiEndpoint) {
      setCurrentPage(1);
      setItems([]);
      setInitialized(false);
    }
    prevApiEndpointRef.current = apiEndpoint;
  }, [apiEndpoint, hasPagination]);
  useEffect(() => {
    if (!hasPagination) return;
    if (!initialized) {
      setInitialized(true);
      const initialPage = getInitialPage();
      setCurrentPage(initialPage);

      // If page is 1 and we have initial data from server, use it without re-fetching
      if (initialPage === 1 && initialItems.length > 0) {
        setItems(initialItems);
        // Use total-count from collection metadata if available, otherwise fetch from API
        const metaItem = initialItems.find((item_0: TransformedNewsItem) => item_0._isCollectionMeta);
        const serverTotalCount = metaItem?.['total-count'] as number | undefined;
        if (serverTotalCount && serverTotalCount > 0) {
          setTotalItems(serverTotalCount);
        } else if (apiEndpoint) {
          // Fetch just the first page to get accurate total-count from API
          const offset_0 = 0;
          fetch(`/api/paginated-collection?endpoint=${encodeURIComponent(apiEndpoint)}&limit=${currentPageSize}&offset=${offset_0}`).then(res => res.ok ? res.json() : null).then(data_1 => {
            if (data_1?.['total-count']) {
              setTotalItems(data_1['total-count']);
            }
          }).catch(() => {}); // Silently fail — pagination will still work on page change
        }
        return;
      }

      // If page > 1 (from URL) or no initial items, fetch the data
      if (apiEndpoint) {
        fetchPaginatedData(initialPage, currentPageSize, false);
      } else if (initialItems.length > 0) {
        setItems(initialItems);
        setTotalItems(initialItems.length);
      }
    }
  }, [apiEndpoint, currentPageSize, fetchPaginatedData, getInitialPage, hasPagination, initialItems, initialized]);
  useEffect(() => {
    if (!hasPagination) return;
    setCurrentPageSize(pageSize);
  }, [hasPagination, pageSize]);

  // Handle browser back/forward navigation for pagination
  useEffect(() => {
    if (!hasPagination || !pagination) return;
    const handlePopState = () => {
      const url_0 = new URL(window.location.href);
      const pageParam_0 = url_0.searchParams.get('page');
      const page_1 = pageParam_0 ? parseInt(pageParam_0, 10) : 1;
      if (!isNaN(page_1) && page_1 >= 1 && page_1 !== currentPage) {
        setCurrentPage(page_1);
        fetchPaginatedData(page_1, currentPageSize, false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentPage, currentPageSize, fetchPaginatedData, hasPagination, pagination]);

  // If pagination is enabled, use the PaginatedColumn wrapper
  if (hasPagination && pagination) {
    const paginatedCollectionsData = new Map(collectionsData);
    // Always use normalized key to match how ColumnContentRenderer looks up data
    const normalizedApiEndpoint = apiEndpoint ? normalizeApiEndpoint(apiEndpoint) : dataBindingKey;
    if (normalizedApiEndpoint && items.length > 0) {
      paginatedCollectionsData.set(normalizedApiEndpoint, items);
    }
    const getWidthClass = (width: {
      mobile: number;
      tablet: number;
      desktop: number;
    }) => {
      const mobileClass = colSpanMap[width.mobile] || 'col-span-12';
      const tabletClass = mdColSpanMap[width.tablet] || 'md:col-span-12';
      const desktopClass = lgColSpanMap[width.desktop] || 'lg:col-span-12';
      return `${mobileClass} ${tabletClass} ${desktopClass}`;
    };

    // Calculate alignment class for responsive breakpoints
    // Since we use flex-col, horizontal alignment uses items-* classes
    const getAlignmentClass = () => {
      if (!column.justifyContent) return '';

      // For flex-col containers, use items-* for horizontal alignment
      const alignMap: Record<string, string> = {
        'flex-start': 'items-start',
        'center': 'items-center',
        'flex-end': 'items-end',
        'space-between': 'items-stretch',
        'space-around': 'items-stretch',
        'space-evenly': 'items-stretch'
      };
      const mdAlignMap: Record<string, string> = {
        'flex-start': 'md:items-start',
        'center': 'md:items-center',
        'flex-end': 'md:items-end',
        'space-between': 'md:items-stretch',
        'space-around': 'md:items-stretch',
        'space-evenly': 'md:items-stretch'
      };
      const lgAlignMap: Record<string, string> = {
        'flex-start': 'lg:items-start',
        'center': 'lg:items-center',
        'flex-end': 'lg:items-end',
        'space-between': 'lg:items-stretch',
        'space-around': 'lg:items-stretch',
        'space-evenly': 'lg:items-stretch'
      };
      const classes: string[] = [];
      if (column.justifyContent.mobile) {
        classes.push(alignMap[column.justifyContent.mobile] || '');
      }
      if (column.justifyContent.tablet) {
        classes.push(mdAlignMap[column.justifyContent.tablet] || '');
      }
      if (column.justifyContent.desktop) {
        classes.push(lgAlignMap[column.justifyContent.desktop] || '');
      }
      return classes.filter(Boolean).join(' ');
    };
    const hasContent = column.components?.length || column.tabBar || column.columns?.length || column.rows?.length;
    const totalPages = Math.min(Math.max(1, Math.ceil(totalItems / currentPageSize)), 500);

    // Check if there's no data to display - only after initialization is complete
    // This prevents showing "No Data" message during initial load
    const hasNoData = initialized && items.length === 0 && !loading;

    // Build sticky styles from column config via shared utility
    const stickyStyles = getStickyStyles({
      sticky: column.sticky,
      stickyTop: column.stickyTop,
      hasTabBar: !!column.tabBar
    });

    // Build background styles including backgroundImage if enabled
    const backgroundStyles: React.CSSProperties = {
      backgroundColor: column.background || 'transparent',
      padding: column.padding || undefined,
      ...stickyStyles
    };

    // Add backgroundImage if enabled
    if (column.backgroundImage?.enabled && column.backgroundImage?.url) {
      backgroundStyles.backgroundImage = `url(${column.backgroundImage.url})`;
      backgroundStyles.backgroundSize = 'cover';
      backgroundStyles.backgroundPosition = 'center';
      backgroundStyles.backgroundRepeat = 'no-repeat';
    }
    return <div ref={columnRef} className={`paginated-column ${getWidthClass(column.width)} ${getAlignmentClass()} flex flex-col`} style={backgroundStyles}>
        {hasNoData ? <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 dark:bg-gray-900">
            <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">
              தரவு இல்லை
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-center">
              இந்தப் பிரிவில் தற்போது எந்தச் செய்திகளும் இல்லை
            </p>
          </div> : <ColumnContentRenderer column={column} collectionsData={paginatedCollectionsData} dynamicTabsMap={dynamicTabsMap} isLastColumn={isLastColumn} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} skipOuterWrapper={true} parentColumnData={parentColumnData} dataStartIndex={dataStartIndex} priority={priority} />}

        {pagination.enabled && pagination.type === 'numbered' && totalItems > 0 && <AstroPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}

        {pagination.enabled && pagination.type !== 'numbered' && totalItems > 0 && <PaginationPanel pagination={pagination} currentPage={currentPage} totalItems={totalItems} pageSize={currentPageSize} onPageChange={handlePageChange} onPageSizeChange={pagination.showSizeChanger ? handlePageSizeChange : undefined} loading={loading} />}
      </div>;
  }

  // Otherwise, render the column content directly
  return <ColumnContentRenderer column={column} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} isLastColumn={isLastColumn} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} parentColumnData={parentColumnData} dataStartIndex={dataStartIndex} priority={priority} />;
}